"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Input from "@/components/library/Input";
import {AgencyVerificationStatus} from "@/enums/agencies";
import {useAlert} from "@/hooks/useAlert";
import {useOrg} from "@/hooks/useOrg";
import {apiFetch} from "@/lib/api";
import {OrgLegalNameCheck} from "@/types/org";
import {URL_REG_EXP} from "@/utils/common/general";
import {legalNamesMatch} from "@/utils/verification/legalName";
import {OnboardingStepHeader} from "./OnboardingStepHeader";
import {StepFooter} from "./StepFooter";

interface PhoneVerificationStepProps {
   readonly onBack?: () => void;
   readonly onNext: () => void;
}

interface VerificationStartResponse {
   success: boolean;
   matched?: boolean;
   messageKey?: string;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

export function PhoneVerificationStep({onBack, onNext}: PhoneVerificationStepProps) {
   const {t} = useTranslation("onboarding");
   const {setAlert} = useAlert();
   const {org, refreshOrg, saveOrg} = useOrg();
   const [legalName, setLegalName] = useState(org?.name ?? "");
   const [websiteUrl, setWebsiteUrl] = useState(org?.details?.website ?? "");
   const [websiteError, setWebsiteError] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [approveError, setApproveError] = useState<string | null>(null);
   const [timedOut, setTimedOut] = useState(false);
   const didAutoStartRef = useRef(false);
   const didFinishRef = useRef(false);

   const finishOnboarding = useCallback(() => {
      if (didFinishRef.current) {
         return;
      }
      didFinishRef.current = true;
      onNext();
   }, [onNext]);

   const check: OrgLegalNameCheck | undefined = org?.details?.legalNameCheck;
   const verificationStatus = org?.details?.agencyVerificationStatus;
   const isPendingScrape = !check || check.status === "pending_scrape";
   const isInProgress =
      check?.status === "started" ||
      verificationStatus === AgencyVerificationStatus.Pending ||
      verificationStatus === AgencyVerificationStatus.Verified;
   const isMismatch = check?.status === "mismatch";
   const isScrapeFailed = check?.status === "failed";
   const isStartFailed = check?.status === "start_failed";
   const isMatched = check?.status === "matched";

   useEffect(() => {
      setLegalName(org?.name ?? "");
   }, [org?.name]);

   useEffect(() => {
      setWebsiteUrl(org?.details?.website ?? "");
   }, [org?.details?.website]);

   useEffect(() => {
      if (isStartFailed) {
         finishOnboarding();
      }
   }, [isStartFailed, finishOnboarding]);

   useEffect(() => {
      if (!isPendingScrape) {
         setTimedOut(false);
         return;
      }

      const startedAt = Date.now();
      void refreshOrg();
      const interval = window.setInterval(() => {
         void refreshOrg();
         if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
            setTimedOut(true);
         }
      }, POLL_INTERVAL_MS);

      return () => window.clearInterval(interval);
   }, [isPendingScrape, refreshOrg]);

   const startVerification = useCallback(async (name: string) => {
      return apiFetch<VerificationStartResponse>("/api/verification/approve", "POST", {
         body: {legalName: name},
         silent: true,
         alwaysReturn: true,
      });
   }, []);

   useEffect(() => {
      if (!isMatched || isInProgress || didAutoStartRef.current) {
         return;
      }

      didAutoStartRef.current = true;
      setIsSaving(true);
      void (async () => {
         try {
            const response = await startVerification(org?.name ?? legalName);
            await refreshOrg();
            if (!response?.success) {
               finishOnboarding();
            }
         } catch (error) {
            console.error("Failed to start Twilio verification", error);
            await refreshOrg();
            finishOnboarding();
         } finally {
            setIsSaving(false);
         }
      })();
   }, [isMatched, isInProgress, legalName, org?.name, refreshOrg, startVerification, finishOnboarding]);

   const handleApprove = useCallback(async () => {
      setApproveError(null);

      if (check?.websiteName && !legalNamesMatch(legalName, check.websiteName)) {
         setApproveError(t("phoneVerification.namesMustMatch"));
         return;
      }

      setIsSaving(true);
      try {
         const response = await startVerification(legalName);

         if (response?.success) {
            await refreshOrg();
            onNext();
            return;
         }

         await refreshOrg();
         finishOnboarding();
      } catch (error) {
         console.error("Failed to approve legal name", error);
         finishOnboarding();
      } finally {
         setIsSaving(false);
      }
   }, [legalName, check?.websiteName, t, refreshOrg, onNext, startVerification, finishOnboarding]);

   const handleRecheckWebsite = useCallback(async () => {
      const trimmed = websiteUrl.trim();
      if (!trimmed || !URL_REG_EXP.test(trimmed)) {
         setWebsiteError(true);
         return;
      }

      setWebsiteError(false);
      setIsSaving(true);
      try {
         await saveOrg({
            details: {
               ...org?.details,
               website: trimmed,
            },
         });
         await apiFetch("/api/verification/prepare", "POST", {silent: true});
         await refreshOrg();
      } catch (error) {
         console.error("Failed to recheck website", error);
         setAlert({status: "error", message: t("phoneVerification.approveFailed")});
      } finally {
         setIsSaving(false);
      }
   }, [websiteUrl, org?.details, saveOrg, refreshOrg, setAlert, t]);

   const handleSkip = useCallback(async () => {
      setIsSaving(true);
      try {
         if (legalName.trim() && legalName.trim() !== org?.name) {
            await saveOrg({name: legalName.trim()});
         }
         const response = await apiFetch<{success: boolean}>("/api/verification/skip", "POST", {
            silent: true,
            alwaysReturn: true,
         });
         if (!response?.success) {
            setAlert({status: "error", message: t("phoneVerification.skipFailed")});
            return;
         }
         onNext();
      } catch (error) {
         console.error("Failed to skip phone verification", error);
         setAlert({status: "error", message: t("phoneVerification.skipFailed")});
      } finally {
         setIsSaving(false);
      }
   }, [legalName, org?.name, saveOrg, onNext, setAlert, t]);

   const showCompare = isMismatch || isScrapeFailed;
   const allowSkip = isMismatch || isScrapeFailed || (isPendingScrape && timedOut);
   const skipAsPrimary = isPendingScrape && timedOut;

   return (
      <div className="flex flex-col h-full">
         <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-xl mx-auto space-y-8">
               <OnboardingStepHeader
                  title={t("phoneVerification.title")}
                  description={t("phoneVerification.subtitle")}
               />

               {isPendingScrape && (
                  <p className="text-sm text-muted-foreground text-center" aria-live="polite">
                     {timedOut
                        ? t("phoneVerification.checkingTimedOut")
                        : t("phoneVerification.checkingWebsite")}
                  </p>
               )}

               {isMatched && !isInProgress && (
                  <p className="text-sm text-muted-foreground text-center" aria-live="polite">
                     {t("phoneVerification.startingVerification")}
                  </p>
               )}

               {isInProgress && (
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-2">
                     <p className="text-sm font-medium text-foreground">
                        {t("phoneVerification.inProgressTitle")}
                     </p>
                     <p className="text-sm text-muted-foreground">
                        {t("phoneVerification.inProgressDescription")}
                     </p>
                  </div>
               )}

               {showCompare && (
                  <div className="space-y-6">
                     {isScrapeFailed && (
                        <p className="text-sm text-foreground">{t("phoneVerification.scrapeFailed")}</p>
                     )}
                     {(isMismatch || isScrapeFailed) && (
                        <p className="text-sm text-foreground">{t("phoneVerification.complianceNote")}</p>
                     )}
                     {isScrapeFailed && (
                        <div className="space-y-2">
                           <Input
                              type="url"
                              hideIcon
                              label={t("companyInfo.companyUrl")}
                              value={websiteUrl}
                              onChange={(e) => setWebsiteUrl(e.target.value)}
                              error={websiteError}
                              helperText={websiteError ? t("companyInfo.websiteHelpText") : undefined}
                           />
                           <button
                              type="button"
                              className="text-sm text-primary hover:underline"
                              onClick={() => void handleRecheckWebsite()}
                              disabled={isSaving}
                           >
                              {t("phoneVerification.recheckWebsite")}
                           </button>
                        </div>
                     )}
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-200 p-4 space-y-1">
                           <p className="text-xs uppercase tracking-wide text-500">
                              {t("phoneVerification.foundOnWebsite")}
                           </p>
                           <p className="text-sm font-medium text-foreground">
                              {check?.websiteName || t("phoneVerification.notFound")}
                           </p>
                        </div>
                        <Input
                           type="text"
                           label={t("phoneVerification.youEntered")}
                           value={legalName}
                           onChange={(e) => setLegalName(e.target.value)}
                        />
                     </div>
                     {isMismatch && approveError && (
                        <p className="text-sm text-destructive">{approveError}</p>
                     )}
                  </div>
               )}
            </div>
         </div>

         {allowSkip ? (
            <StepFooter
               onBack={onBack}
               onSkip={handleSkip}
               onNext={skipAsPrimary ? handleSkip : handleApprove}
               nextLabel={skipAsPrimary ? t("wizard.skipForNow") : t("phoneVerification.approve")}
               skipLabel={t("wizard.skipForNow")}
               isSaving={isSaving}
            />
         ) : (
            <StepFooter
               onBack={onBack}
               onNext={onNext}
               nextLabel={t("wizard.done")}
               nextDisabled={isPendingScrape || (isMatched && !isInProgress)}
               isSaving={isSaving}
            />
         )}
      </div>
   );
}

export default PhoneVerificationStep;
